import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import GlobalPlayerBar from './GlobalPlayerBar';
import Footer from './Footer';
import '../styles/App.css';

const Layout = () => {
  const location = useLocation();
  const [activeLink, setActiveLink] = useState(location.pathname);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1448);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 1448);
      
      if (width > 1448) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
    setActiveLink(location.pathname);
  }, [location, isMobile]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, isMobile]);

  const navLinks = [
    { path: '/', label: 'Home', exact: true },
    { path: '/player', label: 'Live Radio', exact: false },
    { path: '/schedule', label: 'Schedule', exact: false },
    { path: '/catchup', label: 'Catchup', exact: false },
    { path: '/podcasts', icon: 'fas fa-film', label: 'Podcasts', exact: false},
    { path: '/blogs', label: 'Blogs', exact: false},
    { path: '/news', label: 'News', exact: false },
    { path: '/contact', label: 'Contact Us', exact: false },
  ];

  const isLinkActive = (linkPath, exact = false) => {
    if (exact) {
      return location.pathname === linkPath;
    }
    return location.pathname.startsWith(linkPath) && linkPath !== '/';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Desktop top navigation bar
  const DesktopTopNav = () => (
    <nav className="top-navbar">
      {/* Logo - Left */}
      <div className="nav-logo">
        <Link to="/">
          <img 
            src="/images/UtterRadioLogo.png" 
            alt="Utter Radio Logo" 
          />
        </Link>
      </div>
      
      {/* Navigation Links - Center */}
      <ul className="nav-links">
        {navLinks.map((link) => (
          <li key={link.path}>
            <Link 
              to={link.path}
              className={isLinkActive(link.path, link.exact) ? 'active' : ''}
              onClick={() => setActiveLink(link.path)}
            >
              <span>{link.label}</span>
            </Link>
          </li>
        ))}
      </ul>
      
      {/* Admin Button - Right */}
      <div className="nav-admin">
        <a 
          href="/admin" 
          target="_blank" 
          rel="noopener noreferrer"
          className="admin-btn"
          title="Admin Panel"
        >
          <i class="fa-solid fa-user"></i>
        </a>
      </div>
    </nav>
  );

  // Mobile header component
  const MobileHeader = () => (
    <header className="mobile-header">
      <button 
        className="mobile-menu-toggle" 
        onClick={toggleMobileMenu}
        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
      >
        <div className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </button>
      
      <div className="mobile-header-right">
        <Link to="/player" className="mobile-header-btn" aria-label="Live Radio">
          <i className="fas fa-play"></i>
          Listen Live
        </Link>
      </div>
    </header>
  );

  // Mobile menu overlay component
  const MobileMenu = () => (
    <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
      <div className="mobile-menu-backdrop" onClick={toggleMobileMenu}></div>
      
      <nav className="mobile-menu">
        <div className="mobile-menu-header">
          <div className="mobile-menu-logo">
            <img 
              src="/images/UtterRadioLogo.png" 
              alt="Utter Radio" 
            />
          </div>
          <button 
            className="mobile-menu-close" 
            onClick={toggleMobileMenu}
            aria-label="Close menu"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="mobile-menu-content">
          <ul className="mobile-nav-links">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link 
                  to={link.path}
                  className={`mobile-nav-link ${isLinkActive(link.path, link.exact) ? 'active' : ''}`}
                  onClick={() => {
                    setActiveLink(link.path);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <span className="mobile-nav-label">{link.label}</span>
                  <i className="fas fa-chevron-right mobile-nav-arrow"></i>
                </Link>
              </li>
            ))}
          </ul>
          
          <div className="mobile-menu-divider"></div>
          
          {/* Admin link in mobile menu */}
          <div className="mobile-menu-admin">
            <a 
              href="/admin" 
              target="_blank" 
              rel="noopener noreferrer"
              className="mobile-nav-link admin-mobile-link"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i class="fa-solid fa-user"></i>
              <span className="mobile-nav-label">Admin</span>
              <i className="fas fa-external-link-alt mobile-nav-arrow"></i>
            </a>
          </div>
          
          <div className="mobile-menu-divider"></div>
          
          <div className="mobile-menu-footer">
            <div className="mobile-copyright">
              <p>&copy; Utter Radio</p>
              <p>All rights reserved</p>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );

  // Mobile admin button (bottom left square)
  const MobileAdminButton = () => (
    <a 
      href="/admin" 
      target="_blank" 
      rel="noopener noreferrer"
      className="mobile-admin-fab"
      title="Admin Panel"
    >
      <i className="fas fa-lock"></i>
    </a>
  );

  return (
    <>
      <div className="layout-container">
        {/* Desktop top navigation */}
        {!isMobile && <DesktopTopNav />}
        
        {/* Mobile header */}
        {isMobile && <MobileHeader />}
        
        {/* Mobile menu overlay */}
        {isMobile && <MobileMenu />}
        
        <main className={`main-content ${isMobile ? 'mobile' : ''} ${isMobileMenuOpen ? 'menu-open' : ''}`}>
          <div className="page-container"> 
            <Outlet />
          </div> 
          <Footer />
        </main>
      </div>
      
      {/* Only show GlobalPlayerBar when mobile menu is closed */}
      {(!isMobile || !isMobileMenuOpen) && <GlobalPlayerBar />}
    </>
  );
};

export default Layout;